import { pgTable, text, serial, integer, boolean, timestamp, jsonb, real, uuid, varchar } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

// Centralized Tenants table mapping (from central database)
export const tenants = pgTable("tenants", {
  id: uuid("id").primaryKey().defaultRandom(),
  name: varchar("name", { length: 255 }).notNull(),
  subscriptionPlan: varchar("subscription_plan", { length: 50 }).default("starter"),
  subscriptionStatus: varchar("subscription_status", { length: 50 }).default("active"),
  aiComputeCredits: integer("ai_compute_credits").default(1000), // AI token credits
  verificationCreditBalance: integer("verification_credit_balance").default(0),
  billingExempt: boolean("billing_exempt").default(false),
  useOwnAiKeys: boolean("use_own_ai_keys").default(false),
  aiXaiApiKey: text("ai_xai_api_key"),
  aiAnthropicApiKey: text("ai_anthropic_api_key"),
  aiAssemblyaiApiKey: text("ai_assemblyai_api_key"),
  aiElevenlabsApiKey: text("ai_elevenlabs_api_key"),
  enabledModules: jsonb("enabled_modules"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertTenantSchema = createInsertSchema(tenants);
export type Tenant = typeof tenants.$inferSelect;
export type InsertTenant = z.infer<typeof insertTenantSchema>;

// AI Usage & Token Deduction Events table
export const aiUsageEvents = pgTable("ai_usage_events", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id"),
  userId: uuid("user_id"),
  provider: varchar("provider", { length: 50 }).notNull().default("xai"),
  model: varchar("model", { length: 100 }).notNull(),
  taskType: varchar("task_type", { length: 50 }).notNull(),
  promptTokens: integer("prompt_tokens").default(0),
  completionTokens: integer("completion_tokens").default(0),
  totalTokens: integer("total_tokens").default(0),
  creditsDeducted: real("credits_deducted").default(0),
  remainingBalance: real("remaining_balance").default(0),
  latencyMs: integer("latency_ms").default(0),
  metadata: jsonb("metadata"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertAiUsageEventSchema = createInsertSchema(aiUsageEvents);
export type AiUsageEvent = typeof aiUsageEvents.$inferSelect;
export type InsertAiUsageEvent = z.infer<typeof insertAiUsageEventSchema>;

// Centralized buildings table mapping (from training_tube database)
export const buildings = pgTable("buildings", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id"),
  name: varchar("name", { length: 255 }).notNull().unique(),
  seapLocationUri: text("seap_location_uri"),
  createdAt: timestamp("created_at").defaultNow().notNull()
});

export const insertBuildingSchema = createInsertSchema(buildings).omit({
  id: true,
  createdAt: true,
});

export type Building = typeof buildings.$inferSelect;
export type InsertBuilding = z.infer<typeof insertBuildingSchema>;

// Centralized User table mapping (from training_tube database)
export const users = pgTable("users", {
  id: uuid("id").primaryKey(),
  name: varchar("name", { length: 255 }).notNull(),
  username: varchar("username", { length: 255 }).notNull().unique(),
  email: varchar("email", { length: 255 }).notNull(),
  password: varchar("password", { length: 255 }), // Can be null if SSO-only
  role: varchar("role", { length: 50 }).notNull(),
  tenantId: uuid("tenant_id"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const insertUserSchema = createInsertSchema(users);
export type User = typeof users.$inferSelect;
export type InsertUser = z.infer<typeof insertUserSchema>;


// System settings schema
export const systemSettings = pgTable("system_settings", {
  key: text("key").primaryKey(),
  value: text("value").notNull(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertSystemSettingSchema = createInsertSchema(systemSettings);
export type SystemSetting = typeof systemSettings.$inferSelect;
export type InsertSystemSetting = z.infer<typeof insertSystemSettingSchema>;

// Audit logs schema
export const auditLogs = pgTable("ooe_audit_logs", {
  id: serial("id").primaryKey(),
  userId: uuid("user_id").notNull(),
  action: text("action").notNull(),
  details: text("details"),
  timestamp: timestamp("timestamp").notNull().defaultNow(),
  ipAddress: text("ip_address"),
});

export const insertAuditLogSchema = createInsertSchema(auditLogs).omit({
  id: true,
  timestamp: true,
});

// Load profile schema
export const loadProfile = z.array(
  z.object({
    loadRange: z.string(),
    hours: z.number().min(0).max(8760),
    percentage: z.number().optional(),
  })
);

// Sensitivity variables schema
export const sensitivityVariable = z.object({
  name: z.string(),
  selected: z.boolean().default(false),
});

export const sensitivityVariables = z.array(sensitivityVariable);

// Pump analysis schema
export const pumpAnalyses = pgTable("pump_analyses", {
  id: serial("id").primaryKey(),
  userId: uuid("user_id").notNull().references(() => users.id),
  tenantId: uuid("tenant_id"), // Tenant scoping for data isolation
  buildingId: uuid("building_id").references(() => buildings.id),
  // Step 1: Pump/Motor Information
  manufacturer: text("manufacturer").notNull(),
  model: text("model").notNull(),
  age: integer("age"),
  head: real("head"),
  flow: real("flow"),
  hp: real("hp"),
  // Nameplate Data (from PRD)
  nameplateHP: real("nameplate_hp"),
  nameplateKW: real("nameplate_kw"),
  nameplateRPM: integer("nameplate_rpm"),
  nameplateFLA: real("nameplate_fla"),
  // Power Draw (from PRD)
  powerDrawAmps: real("power_draw_amps"),
  powerDrawLoadPercentage: real("power_draw_load_percentage"),
  // Current Operating Point
  operatingPoint: jsonb("operating_point").$type<z.infer<typeof operatingPoint>>(),
  // Step 2: Pump Application Details
  application: text("application").notNull(),
  fluidType: text("fluid_type").notNull(),
  rpm: integer("rpm"),
  efficiency: real("efficiency"),
  // Variable Speed (from PRD)
  variableSpeed: text("variable_speed"),
  variableSpeedDetails: text("variable_speed_details"),
  considerReplacement: text("consider_replacement"),
  // Step 3: Load Profile
  pumpSchedule: text("pump_schedule").notNull(),
  // Seasonal operation flag for advanced visualizations
  seasonalOperation: boolean("seasonal_operation").default(false),
  loadProfile: jsonb("load_profile").notNull(),
  // Simplified Load Profile (from PRD)
  loadProfileSimplifiedSummer: real("load_profile_simplified_summer"),
  loadProfileSimplifiedSpringFall: real("load_profile_simplified_spring_fall"),
  loadProfileSimplifiedWinter: real("load_profile_simplified_winter"),
  // Step 4: Operation & Maintenance
  operatingSchedule: text("operating_schedule"),
  operatingScheduleDetails: text("operating_schedule_details"),
  flexibleScheduling: text("flexible_scheduling"),
  flexibleSchedulingConstraints: text("flexible_scheduling_constraints"),
  maintenanceCost: real("maintenance_cost"),
  // Step 5: Electrical Setup
  electricalSetup: text("electrical_setup").notNull(),
  // Step 6: Utility Information
  utilityProvider: text("utility_provider").notNull(),
  utilityRate: real("utility_rate").notNull(),
  tariffSchedule: text("tariff_schedule"),
  serviceType: text("service_type"),
  rateRange: text("rate_range"),
  demandCharges: real("demand_charges"),
  // Step 7: Facility Details
  location: text("location").notNull(),
  facilityType: text("facility_type").notNull(),
  // Step 8: Control & Automation
  controlType: text("control_type").notNull(),
  preferredControl: text("preferred_control"),
  addControls: text("add_controls").notNull(),
  // Step 9: Life-Cycle Costs
  otherCosts: jsonb("other_costs"),
  // Step 10: Additional Data
  datasheetUrl: text("datasheet_url"),
  preferredManufacturers: text("preferred_manufacturers"),
  retrofitConstraints: text("retrofit_constraints"),
  // Step 11: Analytics Parameters
  lifecycleYears: integer("lifecycle_years").default(10),
  discountRate: real("discount_rate").default(3.5),
  inflationRate: real("inflation_rate").default(2.0),
  carbonEmissionFactor: real("carbon_emission_factor").default(0.5),
  sensitivityVariables: jsonb("sensitivity_variables"),
  monteCarloDistributions: jsonb("monte_carlo_distributions"),
  // General fields
  status: text("status").notNull().default("draft"), // draft, submitted, completed
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
  // Results
  results: jsonb("results"),
  // Sharing
  shareToken: text("share_token"),
  shareExpiration: timestamp("share_expiration"),
});

// Define a schema for other costs
export const otherCost = z.object({
  description: z.string().max(255),
  amount: z.number().min(0),
  recurring: z.boolean()
});

export const otherCosts = z.array(otherCost);

// Define schema for Monte Carlo distributions
export const monteCarloDistribution = z.object({
  name: z.string().max(255),
  mean: z.number().min(0),
  stdDev: z.number().min(0)
});

export const monteCarloDistributions = z.array(monteCarloDistribution);

// Operating point schema
export const operatingPoint = z.object({
  flow: z.number().min(0),
  head: z.number().min(0),
  power: z.number().min(0).optional(),
  efficiency: z.number().min(0).max(100).optional()
});

// Seasonal operation flag
export const seasonalOperation = z.boolean().default(false);

export const pumpAnalysisBaseSchema = z.object({
  // Step 1: Pump/Motor Information
  manufacturer: z.string().min(1, { message: "Manufacturer is required" }),
  model: z.string().min(1, { message: "Model is required" }),
  age: z.number().min(0).max(100).optional(),
  head: z.number().min(0).optional(),
  flow: z.number().min(0).optional(),
  hp: z.number().min(0).optional(),
  // Nameplate Data (from PRD)
  nameplateHP: z.number().min(0).optional(),
  nameplateKW: z.number().min(0).optional(),
  nameplateRPM: z.number().min(0).optional(),
  nameplateFLA: z.number().min(0).optional(),
  // Power Draw (from PRD)
  powerDrawAmps: z.number().min(0).optional(),
  powerDrawLoadPercentage: z.number().min(0).max(100).optional(),
  // Current Operating Point
  operatingPoint: operatingPoint.optional(),
  // Step 2: Pump Application Details
  application: z.string().min(1, { message: "Application is required" }),
  fluidType: z.string().min(1, { message: "Fluid type is required" }),
  rpm: z.number().min(0).optional(),
  efficiency: z.number().min(0).max(100).optional(),
  // Variable Speed (from PRD)
  variableSpeed: z.string().optional(),
  variableSpeedDetails: z.string().max(1000).optional(),
  considerReplacement: z.string().optional(),
  // Step 3: Load Profile
  pumpSchedule: z.string().min(1, { message: "Pump schedule is required" }),
  loadProfile: loadProfile,
  // Seasonal operation flag
  seasonalOperation: seasonalOperation,
  // Simplified Load Profile (from PRD)
  loadProfileSimplifiedSummer: z.number().min(0).max(100).optional(),
  loadProfileSimplifiedSpringFall: z.number().min(0).max(100).optional(),
  loadProfileSimplifiedWinter: z.number().min(0).max(100).optional(),
  // Step 4: Operation & Maintenance
  operatingSchedule: z.string().optional(),
  operatingScheduleDetails: z.string().max(1000).optional(),
  flexibleScheduling: z.string().optional(),
  flexibleSchedulingConstraints: z.string().max(1000).optional(),
  maintenanceCost: z.number().min(0).max(100000).optional(),
  // Step 5: Electrical Setup
  electricalSetup: z.string().min(1, { message: "Electrical setup is required" }),
  // Step 6: Utility Information
  utilityProvider: z.string().min(1, { message: "Utility provider is required" }),
  utilityRate: z.number().min(0).max(1, { message: "Utility rate must be between 0 and 1" }),
  tariffSchedule: z.string().max(255).optional(),
  serviceType: z.string().optional(),
  rateRange: z.string().max(1000).optional(),
  demandCharges: z.number().min(0).optional(),
  // Step 7: Facility Details
  location: z.string().min(1, { message: "Location is required" }),
  facilityType: z.string().min(1, { message: "Facility type is required" }),
  // Step 8: Control & Automation
  controlType: z.string().min(1, { message: "Control type is required" }),
  preferredControl: z.string().optional(),
  addControls: z.string().min(1, { message: "Please indicate if you're willing to add controls" }),
  // Step 9: Life-Cycle Costs
  otherCosts: otherCosts.optional(),
  // Step 10: Additional Data
  preferredManufacturers: z.string().max(1000).optional(),
  retrofitConstraints: z.string().max(1000).optional(),
  // Step 11: Analytics Parameters
  lifecycleYears: z.number().min(1).max(50).default(10),
  discountRate: z.number().min(0).max(20).default(3.5),
  inflationRate: z.number().min(0).max(10).default(2.0),
  carbonEmissionFactor: z.number().min(0).max(2).default(0.5),
  sensitivityVariables: sensitivityVariables.optional(),
  monteCarloDistributions: monteCarloDistributions.optional(),
});

export const insertPumpAnalysisSchema = createInsertSchema(pumpAnalyses)
  .omit({
    id: true,
    userId: true,
    tenantId: true,
    buildingId: true,
    createdAt: true,
    updatedAt: true,
    status: true,
    results: true,
    shareToken: true,
    shareExpiration: true,
  })
  .merge(pumpAnalysisBaseSchema);

export const draftPumpAnalysisSchema = createInsertSchema(pumpAnalyses).omit({
  id: true,
  userId: true,
  tenantId: true,
  buildingId: true,
  createdAt: true,
  updatedAt: true,
  status: true,
  results: true,
  shareToken: true,
  shareExpiration: true,
});

// Recommendations schema
export const recommendations = pgTable("recommendations", {
  id: serial("id").primaryKey(),
  analysisId: integer("analysis_id").notNull(),
  manufacturer: text("manufacturer").notNull(),
  model: text("model").notNull(),
  head: real("head").notNull(),
  flow: real("flow").notNull(),
  hp: real("hp").notNull(),
  motorEfficiency: real("motor_efficiency").notNull(),
  pumpEfficiency: real("pump_efficiency").notNull(),
  systemEfficiency: real("system_efficiency").notNull(),
  materials: text("materials"),
  controlType: text("control_type"),
  estimatedPrice: real("estimated_price"),
  annualEnergySavings: real("annual_energy_savings"),
  annualCostSavings: real("annual_cost_savings"),
  paybackPeriod: real("payback_period"),
  npv: real("npv"),
  irr: real("irr"),
  powerCurve: jsonb("power_curve"),
  compatibilityScore: real("compatibility_score"),
  carbonReduction: real("carbon_reduction"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const insertRecommendationSchema = createInsertSchema(recommendations).omit({
  id: true,
  createdAt: true,
});

// Type definitions
export type AuditLog = typeof auditLogs.$inferSelect;
export type InsertAuditLog = z.infer<typeof insertAuditLogSchema>;
export type PumpAnalysis = typeof pumpAnalyses.$inferSelect;
export type InsertPumpAnalysis = z.infer<typeof insertPumpAnalysisSchema>;
export type Recommendation = typeof recommendations.$inferSelect;
export type InsertRecommendation = z.infer<typeof insertRecommendationSchema>;
export type LoadProfileItem = z.infer<typeof loadProfile>[0];
export type SensitivityVariable = z.infer<typeof sensitivityVariable>;
export type OperatingPoint = z.infer<typeof operatingPoint>;

// NYSERDA forms queue
export const nyserdaEntries = pgTable("nyserda_entries", {
  id: serial("id").primaryKey(),
  userId: uuid("user_id").notNull().references(() => users.id),
  tenantId: uuid("tenant_id"),
  buildingId: uuid("building_id").references(() => buildings.id),
  analysisId: integer("analysis_id"),
  ecmNumber: text("ecm_number"),
  measureName: text("measure_name"),
  measureDescription: text("measure_description"),
  status: text("status"),
  implementationDate: text("implementation_date"),
  totalProjectCost: real("total_project_cost").default(0),
  incentive: real("incentive").default(0),
  netCost: real("net_cost").default(0),
  electricSavings: real("electric_savings").default(0),
  gasSavings: real("gas_savings").default(0),
  totalEnergySavings: real("total_energy_savings").default(0),
  energyCostSavings: real("energy_cost_savings").default(0),
  simplePayback: real("simple_payback").default(0),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const insertNyserdaEntrySchema = createInsertSchema(nyserdaEntries).omit({
  id: true,
  createdAt: true,
});

export type NyserdaEntry = typeof nyserdaEntries.$inferSelect;
export type InsertNyserdaEntry = z.infer<typeof insertNyserdaEntrySchema>;

// OsEM Proposals (ONIT Onboarding)
export const osemProposals = pgTable("osem_proposals", {
  id: serial("id").primaryKey(),
  userId: uuid("user_id").notNull().references(() => users.id),
  tenantId: uuid("tenant_id"),
  buildingId: uuid("building_id").references(() => buildings.id),
  companyName: text("company_name").notNull(),
  companyDescription: text("company_description"),
  sustainabilityGoals: text("sustainability_goals"),
  pastProjectsHistory: text("past_projects_history"),
  weeklyHours: integer("weekly_hours").default(20),
  onSitePercentage: integer("on_site_percentage").default(50),
  facilities: jsonb("facilities").notNull().default([]), // array of {name, address, equipmentProfile}
  organizationSetup: text("organization_setup"),
  energyManagerAgency: text("energy_manager_agency").default("ONIT Global, Inc. (www.onit.global)"),
  glidepathPlan: text("glidepath_plan"),
  electricUsageKwh: real("electric_usage_kwh"),
  electricCost: real("electric_cost"),
  gasUsageTherms: real("gas_usage_therms"),
  gasCost: real("gas_cost"),
  electricSavingsTarget: real("electric_savings_target"),
  fossilFuelSavingsTarget: real("fossil_fuel_savings_target"),
  executiveSponsorName: text("executive_sponsor_name"),
  executiveSponsorTitle: text("executive_sponsor_title"),
  teamMembers: text("team_members"),
  orgChart: jsonb("org_chart").$type<any[]>().default([]), // array of {id, name, title, managerId}
  onitExpert: text("onit_expert").default("John A. Johnson, PE"),
  potentialProjects: jsonb("potential_projects").default([]), // array of strings
  businessType: text("business_type"),
  equipmentOverview: text("equipment_overview"),
  softwarePlatforms: text("software_platforms").default("ONIT SEAP Platform"),
  staffCulture: text("staff_culture"),
  trainingRequirements: text("training_requirements"),
  targetEnergySavings: text("target_energy_savings").default("10%"),
  seapPlatformTraining: boolean("seap_platform_training").default(true),
  developTrainingMaterial: boolean("develop_training_material").default(true),
  omProceduresSupport: boolean("om_procedures_support").default(true),
  trainingVideosCreation: text("training_videos_creation").default("modest number"),
  additionalTasks: jsonb("additional_tasks").$type<{id?: string, title: string, description: string, hours: number}[]>().default([]),
  taskDeliverables: jsonb("task_deliverables").$type<Record<string, string>>().default({
    task1: "Team Charter",
    task2: "Draft EMP (Mo 3) and Final EMP (Mo 12)",
    task3: "Configured energy dashboard",
    task4: "Audit Report with 20+ ECMs",
    task5: "Baseline & M&V Report",
    task6: "Feasibility reports",
    task7: "Staff certifications",
    task8: "Implementation verification report",
    task9: "Implementation verification report",
    task10: "Handover protocol documentation",
    task11: "Case study and presentation slides"
  }),
  ecmFindings: jsonb("ecm_findings").$type<{
    ecmNumber: string;
    description: string;
    location: string;
    quarter: string;
    installed: string;
    electricSavings: number;
    demandSavings: number;
    fossilFuelSavings: number;
    fossilFuelSource: string;
    costSavings: number;
    projectCost: number;
    incentiveProgram?: string;
    notes?: string;
  }[]>().default([
    { ecmNumber: "1.0", description: "Lighting Control", location: "some local", quarter: "Q1", installed: "N", electricSavings: 57540, demandSavings: 4.06, fossilFuelSavings: 0, fossilFuelSource: "", costSavings: 12658, projectCost: 80583, incentiveProgram: "", notes: "" },
    { ecmNumber: "2.0", description: "Plug Load Computers", location: "-", quarter: "Q1", installed: "Y", electricSavings: 8070, demandSavings: 1.0, fossilFuelSavings: 0, fossilFuelSource: "", costSavings: 1775, projectCost: 200, incentiveProgram: "", notes: "" },
    { ecmNumber: "3.0", description: "Plug Load Fly Traps", location: "-", quarter: "Q1", installed: "Y", electricSavings: 3912, demandSavings: 1.0, fossilFuelSavings: 0, fossilFuelSource: "", costSavings: 862, projectCost: 1000, incentiveProgram: "", notes: "" },
    { ecmNumber: "4.0", description: "Plug load Espresso Machines", location: "-", quarter: "Q1", installed: "Y", electricSavings: 30000, demandSavings: 1.0, fossilFuelSavings: 0, fossilFuelSource: "", costSavings: 6590, projectCost: 200, incentiveProgram: "", notes: "" },
    { ecmNumber: "5.0", description: "Plug Load FETCO Coffee Machines", location: "-", quarter: "Q1", installed: "Y", electricSavings: 1728, demandSavings: 1.0, fossilFuelSavings: 0, fossilFuelSource: "", costSavings: 380, projectCost: 100, incentiveProgram: "", notes: "" },
    { ecmNumber: "6.0", description: "Refrigeration Circ Water Pump", location: "Garage", quarter: "Q1", installed: "N", electricSavings: 12963, demandSavings: 1.0, fossilFuelSavings: 0, fossilFuelSource: "", costSavings: 2852, projectCost: 5613, incentiveProgram: "Con Edison", notes: "" },
    { ecmNumber: "7.0", description: "Garage Heating and Pump", location: "Garage & Roof", quarter: "Q2", installed: "N", electricSavings: 20825, demandSavings: 0.0, fossilFuelSavings: 960, fossilFuelSource: "Natural Gas", costSavings: 19270, projectCost: 19530, incentiveProgram: "Con Edison", notes: "" },
    { ecmNumber: "8.0", description: "PCP Heating and Pump", location: "North Boiler Rm", quarter: "Q3", installed: "N", electricSavings: 17805, demandSavings: 0.0, fossilFuelSavings: 405, fossilFuelSource: "Natural Gas", costSavings: 20460, projectCost: 17055, incentiveProgram: "Con Edison", notes: "" },
    { ecmNumber: "9.0", description: "Main Kitchen Exhaust VFD", location: "Main Kitchen", quarter: "Q4", installed: "N", electricSavings: 15106, demandSavings: 0.0, fossilFuelSavings: 319, fossilFuelSource: "Natural Gas", costSavings: 10500, projectCost: 7316, incentiveProgram: "Con Edison", notes: "" },
    { ecmNumber: "10.0", description: "Grill and Fryer Exhaust Fans", location: "Main Kitchen", quarter: "Q4", installed: "N", electricSavings: 9064, demandSavings: 0.0, fossilFuelSavings: 191.6, fossilFuelSource: "Natural Gas", costSavings: 4664, projectCost: 9130, incentiveProgram: "Con Edison", notes: "" },
    { ecmNumber: "11.0", description: "Laundry Exhaust VFD", location: "Laundry Center", quarter: "Q4", installed: "N", electricSavings: 9422, demandSavings: 0.0, fossilFuelSavings: 0, fossilFuelSource: "", costSavings: 2072, projectCost: 8125, incentiveProgram: "Con Edison", notes: "" },
    { ecmNumber: "12.0", description: "SEAP EMIS-ASO / BMS", location: "Roof", quarter: "Q4", installed: "N", electricSavings: 390000, demandSavings: 1.0, fossilFuelSavings: 29400, fossilFuelSource: "Natural Gas", costSavings: 143000, projectCost: 70000, incentiveProgram: "Con Edison", notes: "" }
  ]),
  glidepathEntries: jsonb("glidepath_entries").$type<{
    ecmNumber?: string;
    initiative: string;
    status: string;
    plan: string;
    stepsToComplete: string;
    benefitVsCost: string;
    cashGeneration: string;
    comments: string;
  }[]>().default([
    { initiative: "ECM: Kitchen and Laundry Fans and Controls. Upgrades designed to improve reliability while reducing energy costs.", status: "ECM study shows promise of savings from change out of fans in concert with new controls integrated to SEAP BAS", plan: "Decide on budgeting for these fan and controls projects.", stepsToComplete: "1) Engage ONIT in engineering and design of systems; 2) Apply for Rebates; 3) Purchase equipment and install", benefitVsCost: "Reduced Energy Costs with initial outlay of $37K. Many of these fans are nearing the end of their life expectancy and will need replacement. 12-48 month return on investment.", cashGeneration: "105000", comments: "See ECM documentation for more details." },
    { initiative: "ECM: Pumps. Upgrades to improve reliability while reducing energy costs.", status: "ECM study shows promise of savings from change out of pumps in concert with new controls integrated to SEAP BAS", plan: "Decide on budgeting for these fan and controls projects.", stepsToComplete: "1) Engage ONIT in engineering and design of systems; 2) Apply for Rebates; 3) Purchase equipment and install", benefitVsCost: "12 month return on investment. 15 year life.", cashGeneration: "638000", comments: "See ECM documentation for more details." },
    { initiative: "ECM: Lighting Control. Upgrades to reduced energy costs.", status: "35% Installation completed", plan: "Plan to continue installing controls throughout the 225 lighting zones.", stepsToComplete: "1) Continue purchasing specified controls; 2) Install controls as time permits in order to meet LL88 requirements.", benefitVsCost: "6 to 72 month return on investment. 15 year life.", cashGeneration: "190000", comments: "See ECM documentation for more details. LL88 compliance requirement." },
    { initiative: "ECM: Plug Loads. Upgrades to reduce energy costs.", status: "Computer settings changed to deploy reduced energy usage. Partial Plug Load Switches Purchased with mobile app proven to work for existing WIFI", plan: "Plan to continue installing Plug Load Controls throughout facility for Identified items such as bug lights and small refrigeration.", stepsToComplete: "1) Continue purchasing specified controls; 2) Install controls and program for scheduled use.", benefitVsCost: "6-24 month return on investment. 15 year life.", cashGeneration: "=9600*15", comments: "See ECM documentation for more details." },
    { initiative: "ECM: Laundry Center - Work procedures to reduce energy, chemicals.", status: "Work process procedure was updated. ONIT developed Laundry Center Application for use by workers - Presently running on a server available as a prototype.", plan: "Plan to engage ONIT to enhance the prototype application to include workflow of machines to dryers, and integration of machine kWh from RTEM installation as part of BAS. ROTB to engage ECOLAB to test new chemicals that hold promise of 30% reduction of chemicals and energy.", stepsToComplete: "1) Engage ONIT to harden existing working prototype for use on local computer; 2) Finish wiring and programming of computing devices for machine monitoring and integrate to ONIT SEAP", benefitVsCost: "12 month ROI assuming initial $20,000 on top of NYSERDA present investment. Ten year life.", cashGeneration: "200000", comments: "See Report on Laundry and AI software prototype. CTQs, Workflow efficiency" },
    { initiative: "ECM: EMIS-ASO (Energy Management Information System-Automated System Optimization) with FDD (Fault Detection and Diagnostics). Building Automation System (BAS) controllers for control and monitoring of all systems. Upgrade in AI based intelligence holds promise of reducing peak load, reducing maintenance and emergency repair costs, reducing energy usage, improving customer comfort.", status: "ONIT created drawings for wiring OsEM Task 8; ROTB ordered many parts; ROTB ran 50% of Wires; ONIT programmed twelve CT devices to ONIT SEAP that are operational as hardened proof of IPMVP M&V and kWh Energy and NOAA weather forecast data capture, use, and energy saving reports. ONIT created through programming detailed space and event calendars for ROTB to enable seamless transfer of Salesforce data to ONIT SEAP - APIs are ready. SEAP BAS working for two walk-in refrigeration systems and lighting systems. Virtual Points for Fault Detection and M&V operational for these systems.", plan: "Plan to have BMS specifications and points and JACE hardware available for ONIT to then program a computing device to communicate with JACE and ONIT SEAP. Create tailored FDD equipment templates that are centric to equipment and business needs. ROTB has proposed to NYSERDA training project to train personnel with perpetual training abilities as part of its glidepath to integrating this AI based technology into its operational group.", stepsToComplete: "Complete installing existing wiring package; Steps: 1)ONIT to be engaged to further design, program, including engaged to integrate BMS to SEAP EMIS-ASO and 2) ROTB or contractor to install EMIS points for HVAC RTUs, Refrigeration, Identified ECMs.", benefitVsCost: "Predictive failure of equipment. Reduced energy usage with improved comfort. KPIs of energy, process, and space utilization. Identified $143k per year savings potential at a cost of $70k in design, adaptation programming, and ROTB installation. 15 year life.", cashGeneration: "=143000*15", comments: "See ECM documentation for more details." },
    { initiative: "Training - For Productivity improvement, safety improvement, energy cost reduction, maintenance cost reductions.", status: "Detailed videos on the maintenance of equipment were created and available in a file folder. ONIT developed a Training application for use by workers. Presently running on a server available as a production system for use by ROTB. ROTB Youtube channel created with modest upload of developed training videos. Procedures for key equipment have been captured for use in Training. Alpha prototype of pdf O&M to workflow created.", plan: "Plan - Given the present state of tools, creating workflows, capturing additional O&M manuals for equipment not reviewed (HVAC), create additional training content makes sense to continue given the framework provided. Created a proposal to NYSERDA to continue on Training content creation based on the success of the existing effort.", stepsToComplete: "1) Create more videos, 2) Create AI based training. 3) integrate to YouTube Channel set up for ROTB; 4) Utilize the training application that is was created with the intent of ROTB usage.", benefitVsCost: "Reduced Labor Costs for training. Higher productivity from trained workforce. Improved understanding of equipment operations and maintenance. With a complete training program, labor is known to be reduced by 20%-25% first year improvement in productivity and savings. Increased growth of 10-15% is expected from refresher training. Given $400,000 per year in worker salaries, that equates to $80,000 improvement in productivity and energy savings, conservatively over a ten year training initiative.", cashGeneration: "800000", comments: "See Evidence from Quantitative Studies on Training Programs in Hospitality Report. https://x.com/i/grok/share/iqYkIsbnCy0EncMxzBTCImfCR" }
  ]),
  invoiceCycles: jsonb("invoice_cycles").$type<{
    date: string;
    hours: {
      taskNumber: string;
      onitHours: number;
      rotbHours: number;
    }[];
  }[]>().default([
    {
      date: "2-14",
      hours: [
        { taskNumber: "1a", onitHours: 2, rotbHours: 0 },
        { taskNumber: "2a", onitHours: 3, rotbHours: 0 },
        { taskNumber: "3a", onitHours: 2, rotbHours: 0 },
        { taskNumber: "5", onitHours: 35, rotbHours: 0 },
        { taskNumber: "8", onitHours: 50, rotbHours: 0 },
        { taskNumber: "9", onitHours: 0, rotbHours: 56.9 },
        { taskNumber: "10", onitHours: 3, rotbHours: 0 }
      ]
    },
    {
      date: "4-8",
      hours: [
        { taskNumber: "1a", onitHours: 16, rotbHours: 20 },
        { taskNumber: "2a", onitHours: 21, rotbHours: 0 },
        { taskNumber: "3a", onitHours: 1, rotbHours: 0 },
        { taskNumber: "5", onitHours: 16, rotbHours: 0 },
        { taskNumber: "6", onitHours: 5, rotbHours: 0 },
        { taskNumber: "7", onitHours: 2, rotbHours: 0 },
        { taskNumber: "8", onitHours: 7, rotbHours: 0 },
        { taskNumber: "9", onitHours: 0, rotbHours: 40 },
        { taskNumber: "11", onitHours: 4, rotbHours: 0 }
      ]
    }
  ]),
  projectSchedule: jsonb("project_schedule").$type<{category: string, tasks: {description: string, months: number[]}[]}[]>().default([]),
  lightingEntries: jsonb("lighting_entries").$type<any[]>().default([]),
  hasLightingTemplate: boolean("has_lighting_template").default(false),
  durationMonths: integer("duration_months").default(12),
  budgetTasks: jsonb("budget_tasks").$type<{taskNumber: string, description: string, roleTitle: string, rate: number, hours: number, multiplier: number, onitHours?: number, onitRate?: number, rotbHours?: number, rotbRate?: number}[]>().default([]),
  hourlyRate: real("hourly_rate").default(150),
  nyserdaCostSharePercentage: real("nyserda_cost_share").default(75),
  travelBudget: real("travel_budget").default(0),
  materialsBudget: real("materials_budget").default(0),
  generatedProposal: text("generated_proposal"),
  status: text("status").notNull().default("draft"),
  version: text("version").notNull().default("1"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const osemProposalVersions = pgTable("osem_proposal_versions", {
  id: serial("id").primaryKey(),
  proposalId: integer("proposal_id").notNull(),
  version: text("version").notNull(),
  formData: jsonb("form_data").notNull(),
  generatedProposal: text("generated_proposal"),
  status: text("status").notNull().default("draft"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const insertOsemProposalSchema = createInsertSchema(osemProposals).omit({
  id: true,
  createdAt: true,
  version: true,
});

export type OsemProposal = typeof osemProposals.$inferSelect;
export type InsertOsemProposal = z.infer<typeof insertOsemProposalSchema>;

export const insertOsemProposalVersionSchema = createInsertSchema(osemProposalVersions).omit({
  id: true,
  createdAt: true
});
export type OsemProposalVersion = typeof osemProposalVersions.$inferSelect;
export type InsertOsemProposalVersion = z.infer<typeof insertOsemProposalVersionSchema>;

// Facility ECM Analyses
export const facilityAnalyses = pgTable("facility_analyses", {
  id: serial("id").primaryKey(),
  userId: uuid("user_id").notNull().references(() => users.id),
  tenantId: uuid("tenant_id"),
  buildingId: uuid("building_id").references(() => buildings.id),
  proposalId: integer("proposal_id"),
  taskId: text("task_id"), // link to task (e.g. 'task1', 'task_custom_1')
  ecmType: text("ecm_type").notNull().default("pump"), // 'pump' | 'lighting' | 'boiler' | 'rtu' | 'plug_load' | 'bms' | 'custom'
  name: text("name").notNull(),
  description: text("description"),
  status: text("status").notNull().default("draft"),
  isEcm: boolean("is_ecm").default(true),
  linkToDeve: boolean("link_to_deve").default(false),
  improvementType: text("improvement_type").default("ECM"),
  projectCost: real("project_cost").default(0),
  incentive: real("incentive").default(0),
  electricSavingsKwh: real("electric_savings_kwh").default(0),
  demandSavingsKw: real("demand_savings_kw").default(0),
  fossilFuelSavingsMmbtu: real("fossil_fuel_savings_mmbtu").default(0),
  fossilFuelSource: text("fossil_fuel_source").default("Natural Gas"),
  energyCostSavings: real("energy_cost_savings").default(0),
  operationalSavings: real("operational_savings").default(0),
  simplePayback: real("simple_payback").default(0),
  calculationsData: jsonb("calculations_data").default({}),
  attachments: jsonb("attachments").$type<string[]>().default([]),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertFacilityAnalysisSchema = createInsertSchema(facilityAnalyses).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type FacilityAnalysis = typeof facilityAnalyses.$inferSelect;
export type InsertFacilityAnalysis = z.infer<typeof insertFacilityAnalysisSchema>;

// Spreadsheet Template Library
export const spreadsheetTemplates = pgTable("spreadsheet_templates", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  description: text("description"),
  filePath: text("file_path").notNull(),
  systemType: text("system_type").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull()
});

export const insertSpreadsheetTemplateSchema = createInsertSchema(spreadsheetTemplates).omit({
  id: true,
  createdAt: true,
});
export type SpreadsheetTemplate = typeof spreadsheetTemplates.$inferSelect;
export type InsertSpreadsheetTemplate = z.infer<typeof insertSpreadsheetTemplateSchema>;

// Custom Engineering Studies
export const customEngineeringStudies = pgTable("custom_engineering_studies", {
  id: serial("id").primaryKey(),
  proposalId: integer("proposal_id").references(() => osemProposals.id),
  taskId: text("task_id").notNull(),
  systemType: text("system_type").notNull(),
  chatHistory: jsonb("chat_history").$type<any[]>().notNull().default([]),
  extractedAssumptions: jsonb("extracted_assumptions").notNull().default({}),
  referenceContexts: jsonb("reference_contexts").$type<{fileName: string, filePath: string}[]>().notNull().default([]),
  templateExcelId: integer("template_excel_id").references(() => spreadsheetTemplates.id),
  reportMarkdown: text("report_markdown"),
  excelFilePath: text("excel_file_path"),
  docxFilePath: text("docx_file_path"),
  createdAt: timestamp("created_at").defaultNow().notNull()
});

export const insertCustomEngineeringStudySchema = createInsertSchema(customEngineeringStudies).omit({
  id: true,
  createdAt: true,
});
export type CustomEngineeringStudy = typeof customEngineeringStudies.$inferSelect;
export type InsertCustomEngineeringStudy = z.infer<typeof insertCustomEngineeringStudySchema>;

